import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  FaComments,
  FaGlobe,
  FaMobileAlt,
  FaNetworkWired,
  FaRobot,
  FaShoppingCart,
  FaSpider,
  FaToolbox,
} from "react-icons/fa";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "../components/utilities/navbar";
import Footer from "./utilities/footer";
import { theme } from "../components/utilities/theme";

const MotionBox = motion.create(Box);
const MotionModalContent = motion.create(ModalContent);

const projects = [
  {
    title: "Support Assistant",
    category: "Automation",
    icon: FaRobot,
    desc: "Custom assistant workflows for faster customer support, lead answers, and structured handoff.",
    outcomes: ["Reduced repetitive responses", "Business-context answers", "Human escalation path"],
    clients: [{ name: "Rainbow Touch", url: "https://rainbowtouchindia.com" }],
    color: "#E83E8C",
  },
  {
    title: "Commerce Platform",
    category: "Product",
    icon: FaShoppingCart,
    desc: "A scalable shopping experience with payments, product flows, and operational support.",
    outcomes: ["Conversion-ready storefront", "Inventory-aware workflows", "Secure checkout path"],
    clients: [{ name: "Rainbow Touch", url: "https://rainbowtouchindia.com" }],
    color: "#FFB400",
  },
  {
    title: "Mobile Product MVP",
    category: "Product",
    icon: FaMobileAlt,
    desc: "Cross-platform mobile experience shaped around focused user journeys and launch readiness.",
    outcomes: ["App-ready flows", "Device-friendly UI", "Backend integration plan"],
    clients: [],
    color: "#008BFF",
  },
  {
    title: "Conversion-Ready Websites",
    category: "Web",
    icon: FaGlobe,
    desc: "Responsive sites with clear service paths, conversion structure, and room for automation.",
    outcomes: ["Sharper positioning", "Lead capture paths", "Fast responsive pages"],
    clients: [
      { name: "Rainbow Touch", url: "https://rainbowtouchindia.com" },
      { name: "HoriSun Renewables", url: "https://horisunrenewables.com" },
    ],
    color: "#FF6F20",
  },
  {
    title: "API Gateway Layer",
    category: "Infrastructure",
    icon: FaNetworkWired,
    desc: "Routing, auth, and integration foundations for apps, services, and automated workflows.",
    outcomes: ["Secure API surface", "Cleaner integrations", "Monitoring-ready services"],
    clients: [],
    color: "#00C2CB",
  },
  {
    title: "Utility SDKs",
    category: "Infrastructure",
    icon: FaToolbox,
    desc: "Reusable development kits that speed up teams and standardize product capabilities.",
    outcomes: ["Reusable modules", "Faster development", "Consistent implementation"],
    clients: [{ name: "Rainbow Touch", url: "https://rainbowtouchindia.com" }],
    color: "#5BC0DE",
  },
  {
    title: "Data Extraction Pipeline",
    category: "Automation",
    icon: FaSpider,
    desc: "Scraping and cleanup flows for structured business data, lead lists, and market insight.",
    outcomes: ["Automated collection", "Cleaner datasets", "Repeatable exports"],
    clients: [],
    color: "#9B59B6",
  },
  {
    title: "Strategy Consultation",
    category: "Strategy",
    icon: FaComments,
    desc: "Technical roadmaps for teams deciding where automation, product engineering, or system upgrades should start.",
    outcomes: ["Prioritized roadmap", "Architecture direction", "Execution clarity"],
    clients: [
      { name: "Social Matic", url: "https://socialmatic.au" },
      { name: "Rainbow Touch", url: "https://rainbowtouchindia.com" },
    ],
    color: "#D9534F",
  },
];

export default function Portfolio() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const { pageBg, cardBg, textColor, textSecondary, accentColor, accentCyan } =
    theme.colors;

  const categories = useMemo(
    () => ["All", ...new Set(projects.map((project) => project.category))],
    []
  );
  const visibleProjects = projects.filter(
    (project) => activeCategory === "All" || project.category === activeCategory
  );

  const openClients = (project) => {
    setSelectedProject(project);
    onOpen();
  };

  return (
    <Box bg={pageBg} color={textColor} minH="100vh">
      <Navbar />

      <MotionBox
        id="main-content"
        as="main"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 12, md: 16 }}>
          <Stack direction={{ base: "column", lg: "row" }} spacing={8} align="end" mb={10}>
            <Box flex="1">
              <Badge
                bg="rgba(0, 255, 208, 0.1)"
                color={accentCyan}
                border="1px solid rgba(0, 255, 208, 0.28)"
                borderRadius="full"
                px={4}
                py={1}
                mb={5}
              >
                Portfolio
              </Badge>
              <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.08" mb={5}>
                Proof of product thinking, automation, and systems work.
              </Heading>
              <Text color={textSecondary} fontSize={{ base: "md", md: "xl" }} lineHeight="1.8">
                Explore work across assistants, automation, APIs, web products,
                mobile experiences, and strategic technical consulting.
              </Text>
            </Box>
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={7}
              _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}
            >
              Discuss a Similar Build
            </Button>
          </Stack>

          <HStack spacing={2} overflowX="auto" pb={2} mb={8}>
            {categories.map((category) => {
              const isActive = activeCategory === category;
              return (
                <Button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  size="sm"
                  borderRadius="full"
                  px={4}
                  flexShrink={0}
                  bg={isActive ? accentColor : "rgba(255, 255, 255, 0.06)"}
                  color={isActive ? "white" : textColor}
                  border="1px solid"
                  borderColor={isActive ? accentColor : "rgba(255, 255, 255, 0.14)"}
                  _hover={{
                    bg: isActive ? "#ff853d" : "rgba(255, 255, 255, 0.1)",
                    borderColor: isActive ? "#ff853d" : "rgba(0, 255, 208, 0.4)",
                  }}
                >
                  {category}
                </Button>
              );
            })}
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 3, "2xl": 4 }} spacing={5}>
            {visibleProjects.map((project) => (
              <Box
                key={project.title}
                bg={cardBg}
                border="1px solid rgba(255, 255, 255, 0.12)"
                borderRadius="xl"
                p={6}
                minH="100%"
                transition="all 0.2s"
                _hover={{ transform: "translateY(-4px)", borderColor: `${project.color}88` }}
              >
                <Flex align="center" justify="space-between" mb={5}>
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="lg"
                    bg={`${project.color}1A`}
                    border="1px solid"
                    borderColor={`${project.color}55`}
                  >
                    <Icon as={project.icon} color={project.color} boxSize={6} />
                  </Flex>
                  <Badge color={project.color} bg={`${project.color}18`} borderRadius="full">
                    {project.category}
                  </Badge>
                </Flex>

                <Heading size="md" mb={3}>
                  {project.title}
                </Heading>
                <Text color={textSecondary} lineHeight="1.7" mb={5}>
                  {project.desc}
                </Text>

                <VStack align="stretch" spacing={2} mb={6}>
                  {project.outcomes.map((outcome) => (
                    <HStack key={outcome} align="start" spacing={3}>
                      <CheckCircleIcon color={project.color} mt="3px" />
                      <Text color={textSecondary} fontSize="sm">
                        {outcome}
                      </Text>
                    </HStack>
                  ))}
                </VStack>

                <Flex
                  align="center"
                  justify="space-between"
                  pt={4}
                  borderTop="1px solid rgba(255, 255, 255, 0.1)"
                >
                  <Text color={textSecondary} fontSize="sm">
                    {project.clients.length
                      ? `${project.clients.length} client reference${project.clients.length > 1 ? "s" : ""}`
                      : "Private or internal delivery"}
                  </Text>
                  {project.clients.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      color={project.color}
                      rightIcon={<ArrowForwardIcon />}
                      onClick={() => openClients(project)}
                      _hover={{ bg: `${project.color}14` }}
                    >
                      Clients
                    </Button>
                  )}
                </Flex>
              </Box>
            ))}
          </SimpleGrid>
        </Container>

        <Box bg="rgba(16, 25, 38, 0.95)" py={{ base: 12, md: 16 }}>
          <Container maxW="850px" px={{ base: 4, md: 8 }} textAlign="center">
            <Heading size="xl" mb={4}>
              Want the next case study to be yours?
            </Heading>
            <Text color={textSecondary} fontSize="lg" lineHeight="1.8" mb={7}>
              We can help scope the assistant, app, gateway, or automation workflow
              that gets you from idea to useful product.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={accentColor}
              color="white"
              rightIcon={<ArrowForwardIcon />}
              borderRadius="lg"
              px={7}
              _hover={{ bg: "#ff853d" }}
            >
              Start a Project
            </Button>
          </Container>
        </Box>
      </MotionBox>

      <AnimatePresence>
        {selectedProject && (
          <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
            <ModalOverlay bg="rgba(18,24,40,0.85)" />
            <MotionModalContent
              borderRadius="xl"
              bg={pageBg}
              border="1px solid rgba(255, 255, 255, 0.12)"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.2 }}
            >
              <ModalHeader color={selectedProject.color}>
                {selectedProject.title} clients
              </ModalHeader>
              <ModalCloseButton color={accentColor} />
              <ModalBody pb={6}>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                  {selectedProject.clients.map((client) => (
                    <Link
                      key={client.name}
                      href={client.url}
                      isExternal
                      p={5}
                      bg={cardBg}
                      border="1px solid rgba(255, 255, 255, 0.12)"
                      borderRadius="xl"
                      color={textColor}
                      fontWeight="bold"
                      _hover={{
                        textDecoration: "none",
                        borderColor: `${selectedProject.color}88`,
                        color: selectedProject.color,
                      }}
                    >
                      {client.name}
                    </Link>
                  ))}
                </SimpleGrid>
              </ModalBody>
            </MotionModalContent>
          </Modal>
        )}
      </AnimatePresence>

      <Footer />
    </Box>
  );
}
